import React, { useState, useEffect, useCallback, useContext, useRef } from 'react'
import {
    IconButton, Menu, MenuItem, Skeleton, Typography, Stack,
    Box, Button, CircularProgress, Modal, Paper, Divider, InputBase
} from '@mui/material'
import { Close, Refresh } from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import { LuCodeXml } from "react-icons/lu"
import { blue } from "@mui/material/colors"
import { NodeRendererProps, Tree, TreeApi } from 'react-arborist'
import { v4 as uuidv4 } from 'uuid'

import { isPrivate } from '../../../utils/config'
import { modalStyle } from '../../../styles/js/common'
import { post } from '../../../utils/http'
import { ImageData, FileListNodeProp } from '../../../utils/types'
import { ViewerContext } from './context'
import { handleAnnoCheckbox } from './common'


interface GeoJSONFeature {
    type: "Feature";
    id: string | null;
    geometry: {
        type: "Polygon";
        coordinates: number[][][];
    };
    properties: {
        objectType: string;
        classification: {
            name: string | null;
            color: string | null;
        };
    };
}

const Node = ({ node, style }: NodeRendererProps<any>) => {
    return (
        <div style={style} className='node-container cursor-pointer' onClick={() => node.toggle()}>   
            {node.data.is_loading
                ? <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                : <input
                    type="checkbox"
                    className="form-check-input m-0 shadow-none"
                    style={{pointerEvents: 'none'}}
                    value=""
                    checked={node.data.checked}
                    readOnly
                />
            }
            {node.data.name.includes('/')
                ? <LuCodeXml style={{pointerEvents: 'none', fontSize: '1.5rem', color: blue[700]}} />
                : null
            }
            <Typography className='node-text' title={node.data.name.split('/').pop()}>
                {node.data.name.split('/').pop()}
            </Typography>
        </div>
    )
}


interface XmlListProps {
    imageData: ImageData
    list: string[]
    refreshList: Function
    renderXMLData: Function
    removeXMLData: Function
    addXMLClassData: Function
    removeXMLClassData: Function    
}

const XmlList = ({
    imageData,
    list,
    refreshList,
    renderXMLData,
    removeXMLData,
    addXMLClassData,
    removeXMLClassData    
}: XmlListProps) => {
    const treeRef = useRef<TreeApi<FileListNodeProp>>(null)

    const { xmlClasses, xmlClassFilter, xmlNames, resetMap, readonly, toggleLoading } = useContext(ViewerContext)

    const [xmlFiles, setXmlFiles] = useState<FileListNodeProp[]>([])
    const [xmlDefault, setXmlDefault] = useState<boolean>(false)
    const [isListLoading, setIsListLoading] = useState<boolean>(false)
    const [loader, setLoader] = useState<boolean>(false)
    const [xmlAnchorEl, setXmlAnchorEl] = useState<null | HTMLElement>(null)
    const [searchKey, setSearchKey] = useState<string>('')
    const [selectedXml, setSelectedXml] = useState<string>('')
    const [focusNodeId, setFocusNodeId] = useState<string>('')  // eslint-disable-line
    const [boxHeight, setBoxHeight] = useState<number>(0)

    const rowHeight = 32
    const maxHeight: number = (window.visualViewport?.height ?? window.innerHeight) - 320

    const xmlMenuOpen = Boolean(xmlAnchorEl)
    const userId = Number(localStorage.getItem('user_id') || '')

    const filterXmlList = useCallback(async () => {
        const xmlList = list.filter((name: string) => (name.length && name.endsWith('.xml') && !(name.endsWith('tilemapresource.xml'))))
        const files = xmlList.map((name: string, index: number) => {
            let classes: FileListNodeProp[] = []
            if (name in xmlClasses) {
                classes = xmlClasses[name].map(x => ({
                    id: uuidv4(),
                    name: x.name,
                    checked: (name in xmlClassFilter) ? !xmlClassFilter[name].includes(x.name) : true,
                    children: []
                }))
            }
            return {
                id: uuidv4(),
                name: name,
                checked: xmlNames.includes(name),
                children: classes
            }
        })
        setXmlFiles(files)
        setIsListLoading(false)
        setBoxHeight(18 + (files.length * rowHeight))
    }, [list, xmlClasses, xmlClassFilter, xmlNames])

    useEffect(() => {
        filterXmlList()
    }, [list])  // eslint-disable-line

    useEffect(() => {
        if (Object.keys(xmlClasses).length) {
            setXmlFiles(prevState => {
                return prevState.map(i => {
                    if (i.name in xmlClasses) {
                        i.is_loading = false
                        if (!i.children!.length) {
                            i.children = xmlClasses[i.name].map(x => ({
                                id: uuidv4(),
                                name: x.name,
                                checked: true,
                                children: []
                            }))
                        } else {
                            i.children = i.children!.map(x => ({...x, checked: (i.name in xmlClassFilter) ? !xmlClassFilter[i.name].includes(x.name) : true }))
                        }
                    }
                    return i
                })
            })
        }
        // classes inside xml created after  XML is fetched from S3
    }, [xmlClasses])  // eslint-disable-line

    useEffect(() => {
        setXmlFiles(prevState => {
            if (!prevState.length) return prevState

            return prevState.map(i => {
                if (xmlNames.includes(i.name)) i.checked = true
                else i.checked = false
                i.children = i.children!.map(x => {
                    if (i.name in xmlClassFilter) {
                        if (xmlClassFilter[i.name].includes(x.name)) x.checked = false
                        else x.checked = true
                    } else x.checked = false
                    return x
                })
                return i
            })
        })
        // Update states using parent data
    }, [xmlNames, xmlClassFilter])

    const handleCloseXMLBtn = () => setXmlAnchorEl(null)

    const openMakeXMLDefault = () => {
        setXmlDefault(true)
        handleCloseXMLBtn()
    }

    const closeMakeXMLDefault = () => setXmlDefault(false)

    const makeXMLDefault = async () => {
        setLoader(true)
        try {
            await post('Annotations/new-base', {
                file_id: imageData.image_unique_id,
                user_id: userId,
                xml_key: selectedXml
            })
            resetMap()
        } catch (err) {
            console.log(err)
        } finally {
            setLoader(false)
            closeMakeXMLDefault()
        }
    }

    const downloadXMLFile = async () => {
        handleCloseXMLBtn()
        try {
            const filename = selectedXml.split('/')[selectedXml.split('/').length - 1] || `${imageData.image_unique_id}.xml`
            const payload = { s3_name: selectedXml.split('/')[0], key: selectedXml }
            const fileExist = await post('S3/file-exist', payload)
            const res = await post('S3/getaccess', { s3_name: payload.s3_name, key: fileExist.key })
            const a = document.createElement('a')
            document.body.appendChild(a)
            a.style.display = 'none'
            a.href = res.url
            a.download = filename
            a.target = '_blank'
            a.click()
            window.URL.revokeObjectURL(res.url)
            a.remove()
        } catch (err) {
            console.log(err)
        }
    }

    const downloadGeoJSONFile = async () => {
        handleCloseXMLBtn()
        toggleLoading('xml-load', 'Generating and Downloading GeoJSON...')
        try {
            let xmlFilename = selectedXml.split('/').pop() || `${imageData.image_unique_id}.xml`
            let geojsonFilename = xmlFilename.replace(/\.xml$/i, '.geojson')

            const payload = { s3_name: selectedXml.split('/')[0], key: selectedXml }
            const fileExist = await post('S3/file-exist', payload)
            const res = await post('S3/getaccess', { s3_name: payload.s3_name, key: fileExist.key })

            // Fetch the XML text content from the signed S3 URL
            const response = await fetch(res.url);
            if (!response.ok) throw new Error(`Failed to fetch XML: ${response.statusText}`);
            const xmlText = await response.text();

            // Parse XML string into a DOM Document
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, "text/xml");

            // Extract data and build GeoJSON
            const features: GeoJSONFeature[] = [];
            const scale = 1;

            const annotations = xmlDoc.getElementsByTagName("Annotation");
            for (let i = 0; i < annotations.length; i++) {
                const annotation = annotations[i];
                const name = annotation.getAttribute("Name");

                const regions = annotation.getElementsByTagName("Region");
                for (let j = 0; j < regions.length; j++) {
                    const region = regions[j];
                    const id = region.getAttribute("Id");

                    const vertices = region.getElementsByTagName("Vertex");
                    const coords: number[][] = [];

                    for (let k = 0; k < vertices.length; k++) {
                        const vertex = vertices[k];
                        const x = scale * parseFloat(vertex.getAttribute("X") || "0");
                        const y = scale * parseFloat(vertex.getAttribute("Y") || "0");
                        coords.push([x, y]);
                    }

                    if (coords.length === 0) continue;

                    // Close polygon if the first and last coordinates don't match
                    const first = coords[0];
                    const last = coords[coords.length - 1];
                    if (first[0] !== last[0] || first[1] !== last[1]) {
                        coords.push([first[0], first[1]]);
                    }

                    features.push({
                        type: "Feature",
                        id: id,
                        geometry: {
                            type: "Polygon",
                            coordinates: [coords]
                        },
                        properties: {
                            objectType: "annotation",
                            classification: {
                                name: name,
                                color: null
                            }
                        }
                    });
                }
            }

            const geojson = {
                type: "FeatureCollection",
                features: features
            };

            // Create a Blob and trigger the download
            const blob = new Blob([JSON.stringify(geojson)], { type: 'application/geo+json' });
            const objectUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = objectUrl;
            a.download = geojsonFilename;

            document.body.appendChild(a);
            a.click();

            // Clean up DOM and memory
            a.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error("Error converting/downloading GeoJSON:", err);
        } finally {
            toggleLoading('xml-load', '')
        }
    }

    const handleXMLBtn = (e: React.MouseEvent<HTMLButtonElement>) => setXmlAnchorEl(e.currentTarget)

    const isOptEnabled = () => {
        let disable = true
        const restrictedFiles = ['-', 'nuclei_tissue_glands', 'glands', 'tissue_glands', 'grade_renamed_by_prob', 'bcr_risk',
            'cancer_detect_net', 'grid_labels_by_gland', 'stain_net'
        ]
        if (selectedXml && !restrictedFiles.includes(selectedXml)) disable = false
        return disable
    }

    const reloadList = async () => {
        setIsListLoading(true)
        await refreshList()
        setIsListLoading(false)
    }

    const handleXmlNameSearch = (e: any) => setSearchKey(e.target.value)

    const handleCloseXmlNameSearch = () => setSearchKey('')

    const updateTreeUI = () => {
        try {
            if (!treeRef.current) throw Error('empty ref')

            let newHeight = 18
            for (const parent of xmlFiles) {
                newHeight += rowHeight
                if (parent.children) {
                    if (parent.checked) {
                        newHeight += parent.children!.length * rowHeight
                        treeRef.current.open(parent.id)
                    } else treeRef.current.close(parent.id)
                    for (const child of parent.children) {
                        if (child.checked) treeRef.current.open(child.id)
                        else treeRef.current.close(child.id)
                    }
                }
            }
            if (newHeight > maxHeight) setBoxHeight(maxHeight)
            else setBoxHeight(newHeight)
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        updateTreeUI()
    }, [xmlFiles])  // eslint-disable-line

    const onSelect = (nodes: any[]) => {
        if (nodes.length) {
            try {
                handleAnnoCheckbox('')
                const key: string = nodes[0].data.name
                const xmlClass = key.split('/').pop() || ''
                if (key.includes('/')) {
                    // XML Files
                    setSelectedXml(key)
                    if (!xmlNames.includes(key)) {
                        if (xmlClass.length > 4) {
                            if (!(key in xmlClasses)) nodes[0].data.is_loading = true
                            renderXMLData(key)
                        }
                    } else if (xmlClass.length > 4) removeXMLData(key)
                } else {
                    // XML Classes
                    const xmlKey = nodes[0].parent.data.name
                    setSelectedXml(xmlKey)
                    if (xmlKey in xmlClassFilter && xmlClassFilter[xmlKey].includes(xmlClass)) removeXMLClassData(xmlKey, xmlClass)
                    else addXMLClassData(xmlKey, xmlClass)
                }
            } catch (err) {
                console.log(err)
            }
        }
    }

    return (
        <>
            <div className='w-100 mb-3 d-flex justify-content-between align-items-center gap-2'>
                <Paper component="form" sx={{p: '2px 4px', display: 'flex', alignItems: 'center', width: '100%'}}>
                    {!readonly
                        ? <IconButton
                            onClick={handleXMLBtn}
                            id="xml-button"
                            aria-controls={xmlMenuOpen ? 'xml-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={xmlMenuOpen ? 'true' : undefined}
                        >
                            <MenuIcon />
                        </IconButton>
                        : null
                    }
                    <InputBase
                        sx={{ml: 1, flex: 1}}
                        value={searchKey}
                        onChange={handleXmlNameSearch}
                        placeholder="Search"
                        inputProps={{ 'aria-label': 'search xml' }}
                    />
                    <IconButton
                        onClick={handleCloseXmlNameSearch}
                        type="button"
                        sx={{p: '10px'}}
                        disabled={!searchKey.length}
                        aria-label="reset xml search"
                    >
                        <Close />
                    </IconButton>
                    <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                    <IconButton onClick={reloadList} disabled={isListLoading}  aria-label="reload xml list">
                        <Refresh />
                    </IconButton>
                </Paper>
                {!readonly
                    ? <Menu
                        id="xml-menu"
                        anchorEl={xmlAnchorEl}
                        open={xmlMenuOpen}
                        onClose={handleCloseXMLBtn}
                        MenuListProps={{ 'aria-labelledby': 'xml-button' }}
                    >
                        <MenuItem disabled={isOptEnabled()} onClick={openMakeXMLDefault}>+ System Annotations</MenuItem>
                        {isPrivate && <MenuItem disabled={!selectedXml.length} onClick={downloadXMLFile}>Download XML</MenuItem>}
                        {isPrivate && <MenuItem disabled={!selectedXml.length} onClick={downloadGeoJSONFile}>Download GeoJSON</MenuItem>}
                    </Menu>
                    : null
                }
                
            </div>
            {isListLoading
                ? <Stack spacing={1}>
                    {Array.from({ length: 3 }, (_, i) => i).map(i => (<Skeleton key={i} variant="rectangular" height={rowHeight} />))}
                </Stack>
                : xmlFiles.length
                    ?  <Box sx={{width: '100%', maxHeight: 'calc(100vh - 320px)', overflow: 'auto'}}>
                        <Tree
                            ref={treeRef}
                            data={xmlFiles}
                            openByDefault={false}
                            rowHeight={rowHeight}
                            onSelect={onSelect}
                            height={boxHeight}
                            width='auto'
                            indent={40}
                            disableDrag={true}
                            selection={focusNodeId}
                            searchTerm={searchKey}
                        >
                            {Node}
                        </Tree>
                    </Box>
                    : null
            }
            <Modal open={xmlDefault} onClose={closeMakeXMLDefault}>
                <Box sx={modalStyle}>
                    <Box className='d-flex flex-column gap-4' sx={{maxWidth: 500}}>
                        <Typography variant="h5" component="h1">New system annotation data</Typography>
                        <Typography className='text-wrap'>
                            Create new system annotations and diagnostic prediction results using <Typography component="span" color='primary'>{selectedXml.split('/').pop()}</Typography> file.
                        </Typography>
                        <Box className='d-flex gap-3 justify-content-end'>
                            {loader
                                ? <CircularProgress size={24}/>
                                : <>
                                    <Button variant="outlined" onClick={closeMakeXMLDefault}>Close</Button>
                                    <Button variant="contained" onClick={makeXMLDefault}>Confirm</Button>
                                </>
                            }
                            
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    )
}

export default XmlList